import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Download,
  Pause,
  Play,
  RefreshCw,
  Scale,
  Wallet,
  Landmark,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { buildMonthEnds, clearBalanceSheetCache, fetchBalanceSheet, todayMontreal, type MonthEnd } from '../api';
import { balanceSheetKeys, useBalanceSheet } from '../hooks';
import type { BalanceSheetReport, BalanceSheetRow } from '../types';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUserUiPref } from '@/hooks/useUserUiPref';
import { Skeleton } from '@/components/ui/skeleton';
import { toCSV, downloadCSV, type CSVColumn } from '@/lib/csv';

const BS_TABS = [
  { value: 'statement', label: 'Statement' },
  { value: 'comparative', label: 'Comparative' },
] as const;
type BsTab = (typeof BS_TABS)[number]['value'];
const VALID_BS_TABS: readonly BsTab[] = BS_TABS.map((t) => t.value);

function isValidBsTab(v: unknown): v is BsTab {
  return typeof v === 'string' && (VALID_BS_TABS as readonly string[]).includes(v);
}

// GL drill-down spans from well before the report date (balances carry forward).
function glStartFor(asOf: string): string {
  const year = parseInt(asOf.slice(0, 4), 10) || new Date().getFullYear();
  return `${year - 10}-01-01`;
}

export default function BalanceSheetDashboard() {
  const [tabRaw, setTabRaw] = useUserUiPref<BsTab>('balanceSheet.tab', 'statement');
  const tab: BsTab = isValidBsTab(tabRaw) ? tabRaw : 'statement';
  const setTab = (next: BsTab) => setTabRaw(next);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-0 border-b border-border">
        <div className="flex items-center gap-2 pr-4 py-2">
          <Scale className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-semibold">Balance Sheet</h1>
        </div>
        {BS_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.value
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'comparative' ? <ComparativeView /> : <StatementView />}
    </div>
  );
}

// ============================================================================
// Statement view (single as-of date)
// ============================================================================

// Step 3 — module-private error fallback
function BalanceSheetErrorFallback({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3 text-center" role="alert">
      <AlertTriangle className="w-8 h-8 text-destructive" aria-hidden="true" />
      <p className="font-semibold text-sm">Failed to load balance sheet</p>
      <p className="text-xs text-muted-foreground max-w-xs">
        {(error as Error)?.message || 'Unknown error'}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="w-4 h-4 mr-1" aria-hidden="true" />
        Try again
      </Button>
    </div>
  );
}

function StatementView() {
  const queryClient = useQueryClient();
  const [asOf, setAsOf] = useState<string>(() => todayMontreal());

  const { data, isLoading, isFetching, isError, error } = useBalanceSheet(asOf);
  const glStart = useMemo(() => glStartFor(asOf), [asOf]);

  const handleRefresh = async () => {
    try {
      await clearBalanceSheetCache(asOf);
    } catch {
      // best-effort — still refresh the client cache
    }
    queryClient.invalidateQueries({ queryKey: balanceSheetKeys.asOf(asOf) });
  };

  const balanceDelta = data ? data.totalAssets - data.totalLiabilitiesAndEquity : 0;
  const isBalanced = Math.abs(balanceDelta) < 0.01;

  // Step 5 — export CSV for the statement view
  function handleExportCSV() {
    if (!data) return;
    const rows: BalanceSheetRow[] = [...data.assets, ...data.liabilitiesAndEquity];
    const columns: CSVColumn<BalanceSheetRow>[] = [
      { header: 'Name', value: (r) => r.name },
      { header: 'Type', value: (r) => r.type },
      { header: 'Depth', value: (r) => r.depth },
      { header: 'Value', value: (r) => r.value },
    ];
    downloadCSV(`balance-sheet-${asOf}.csv`, toCSV(rows, columns));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">
          {data?.reportDate ? `as of ${formatDate(data.reportDate)}` : ' '}
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-muted-foreground">As of</label>
          <Input
            type="date"
            value={asOf}
            max={todayMontreal()}
            onChange={(e) => e.target.value && setAsOf(e.target.value)}
            className="h-8 w-[160px] text-xs"
          />
          {/* Step 5 — Export CSV button */}
          <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!data}>
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          <Button size="sm" onClick={handleRefresh} disabled={isFetching}>
            <RefreshCw className={cn('w-4 h-4 mr-1', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Step 2 — loading skeleton replacing the spinner */}
      {isLoading && !data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border p-5 space-y-2 bg-background">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-32" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[0, 1].map((i) => (
              <div key={i} className="rounded-xl border bg-background overflow-hidden">
                <div className="bg-muted/50 border-b px-4 py-3">
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="p-4 space-y-2">
                  {[0, 1, 2, 3, 4, 5].map((j) => (
                    <Skeleton key={j} className="h-4 w-full" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isError && !data ? (
        /* Step 3 — error fallback (no data to show) */
        <BalanceSheetErrorFallback
          error={error}
          onRetry={() => queryClient.invalidateQueries({ queryKey: balanceSheetKeys.asOf(asOf) })}
        />
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4">
            <SummaryCard title="Total Assets" value={data?.totalAssets ?? 0} icon={<Wallet className="w-4 h-4 text-primary" />} loading={isLoading} />
            <SummaryCard title="Total Liabilities" value={data?.totalLiabilities ?? 0} icon={<Landmark className="w-4 h-4 text-warning" />} loading={isLoading} />
            <SummaryCard
              title="Total Equity"
              value={data?.totalEquity ?? 0}
              icon={
                // TODO GAP-FE-010: no purple semantic token — using text-secondary-foreground as nearest
                <Scale className="w-4 h-4 text-secondary-foreground" />
              }
              loading={isLoading}
            />
            <BalanceCheckCard delta={balanceDelta} balanced={isBalanced} loading={isLoading} />
          </div>

          {/* Background-refetch error: data is still shown, but surface the error */}
          {isError && data && (
            <BalanceSheetErrorFallback
              error={error}
              onRetry={() => queryClient.invalidateQueries({ queryKey: balanceSheetKeys.asOf(asOf) })}
            />
          )}

          {data && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Assets" total={data.totalAssets} totalLabel="Total Assets" totalClass="text-primary">
                {data.assets.map((row, idx) => (
                  <AccountRow key={idx} row={row} start={glStart} end={asOf} />
                ))}
              </SectionCard>

              <SectionCard
                title="Liabilities & Equity"
                total={data.totalLiabilitiesAndEquity}
                totalLabel="Total Liabilities & Equity"
                // TODO GAP-FE-010: no purple semantic token — using text-secondary-foreground as nearest
                totalClass="text-secondary-foreground"
              >
                {data.liabilitiesAndEquity.map((row, idx) => (
                  <AccountRow key={idx} row={row} start={glStart} end={asOf} />
                ))}
              </SectionCard>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Comparative view (balances across month-ends)
// ============================================================================

interface MonthData {
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  totalLiabilitiesAndEquity: number;
  report: BalanceSheetReport;
}

type SectionKey = 'assets' | 'liabilities' | 'equity';

interface AccountAgg {
  key: string;
  name: string;
  accountId?: string;
  perMonth: Record<string, number>;
  firstSeenIdx: number;
}

// Step 6 / R6 — exported pure helpers for CSV aggregation (also exercised by unit tests)

/** Returns the section total for a given loaded month (0 when month absent). */
export function sectionTotal(section: SectionKey, m?: MonthData): number {
  if (!m) return 0;
  if (section === 'assets') return m.totalAssets;
  if (section === 'liabilities') return m.totalLiabilities;
  return m.totalEquity;
}

/**
 * Aggregates leaf account rows for a section across all loaded months.
 * `newestMonth` is used for the sort-by-latest-balance ordering.
 */
export function aggregate(
  section: SectionKey,
  months: MonthEnd[],
  loaded: Record<string, MonthData>,
  newestMonth: string,
): AccountAgg[] {
  const map = new Map<string, AccountAgg>();
  months.forEach((m, idx) => {
    const data = loaded[m.month];
    if (!data) return;
    const rows = section === 'assets' ? data.report.assets : data.report.liabilitiesAndEquity;
    let inEquity = false;
    for (const r of rows) {
      if (r.type === 'subtotal' && /^total liabilities$/i.test(r.name)) {
        inEquity = true;
        continue;
      }
      if (r.type !== 'item') continue;
      const rowSection: SectionKey = section === 'assets' ? 'assets' : inEquity ? 'equity' : 'liabilities';
      if (rowSection !== section) continue;
      const key = r.accountId || `name:${r.name}`;
      let agg = map.get(key);
      if (!agg) {
        agg = { key, name: r.name, accountId: r.accountId, perMonth: {}, firstSeenIdx: idx };
        map.set(key, agg);
      }
      agg.perMonth[m.month] = (agg.perMonth[m.month] ?? 0) + r.value;
    }
  });
  return [...map.values()].sort((a, b) => {
    const av = Math.abs(a.perMonth[newestMonth] ?? 0);
    const bv = Math.abs(b.perMonth[newestMonth] ?? 0);
    return bv - av || a.firstSeenIdx - b.firstSeenIdx;
  });
}

// Step 6 — helper to build flat rows for comparative CSV export
export function buildComparativeRows(
  months: MonthEnd[],
  loaded: Record<string, MonthData>,
): Array<Record<string, string | number>> {
  if (months.length === 0) return [];

  const newestMonth = months[0].month;
  const oldestMonth = months[months.length - 1].month;

  const sections: { key: SectionKey; label: string }[] = [
    { key: 'assets', label: 'Assets' },
    { key: 'liabilities', label: 'Liabilities' },
    { key: 'equity', label: 'Equity' },
  ];

  const result: Array<Record<string, string | number>> = [];

  // A Change value is only meaningful when BOTH endpoint months are loaded
  // and there are at least two distinct months in range.
  const singlePeriod = newestMonth === oldestMonth;
  const changeOrBlank = (newestLoaded: boolean, oldestLoaded: boolean, diff: number): string | number => {
    if (singlePeriod || !newestLoaded || !oldestLoaded) return '';
    return diff;
  };

  for (const section of sections) {
    // Section total row — guard unloaded months so they emit '' instead of a misleading 0
    const totalRow: Record<string, string | number> = { 'Line item': `Total ${section.label}` };
    for (const m of months) {
      totalRow[m.label] = loaded[m.month] ? sectionTotal(section.key, loaded[m.month]) : '';
    }
    const newestVal = sectionTotal(section.key, loaded[newestMonth]);
    const oldestVal = sectionTotal(section.key, loaded[oldestMonth]);
    totalRow['Change'] = changeOrBlank(!!loaded[newestMonth], !!loaded[oldestMonth], newestVal - oldestVal);
    result.push(totalRow);

    // Detail rows
    for (const agg of aggregate(section.key, months, loaded, newestMonth)) {
      const row: Record<string, string | number> = { 'Line item': agg.name };
      for (const m of months) {
        row[m.label] = loaded[m.month] ? (agg.perMonth[m.month] ?? 0) : '';
      }
      const detailDiff = (agg.perMonth[newestMonth] ?? 0) - (agg.perMonth[oldestMonth] ?? 0);
      row['Change'] = changeOrBlank(!!loaded[newestMonth], !!loaded[oldestMonth], detailDiff);
      result.push(row);
    }
  }

  // Total L&E row
  const leRow: Record<string, string | number> = { 'Line item': 'Total Liabilities & Equity' };
  for (const m of months) {
    leRow[m.label] = loaded[m.month]?.totalLiabilitiesAndEquity ?? '';
  }
  const leNewest = loaded[newestMonth]?.totalLiabilitiesAndEquity ?? 0;
  const leOldest = loaded[oldestMonth]?.totalLiabilitiesAndEquity ?? 0;
  leRow['Change'] = changeOrBlank(!!loaded[newestMonth], !!loaded[oldestMonth], leNewest - leOldest);
  result.push(leRow);

  return result;
}

function ComparativeView() {
  const queryClient = useQueryClient();

  const [months, setMonths] = useState(12);
  const [expanded, setExpanded] = useUserUiPref<string[]>('balanceSheet.comparativeExpanded', ['assets']);
  const [loaded, setLoaded] = useState<Record<string, MonthData>>({});
  const [currentLoading, setCurrentLoading] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  const monthsList: MonthEnd[] = useMemo(() => buildMonthEnds(months), [months]);
  const monthsNewestFirst = useMemo(() => [...monthsList].reverse(), [monthsList]);

  const getNextMonthToLoad = useCallback((): MonthEnd | null => {
    for (const m of monthsNewestFirst) {
      if (!loaded[m.month]) return m;
    }
    return null;
  }, [monthsNewestFirst, loaded]);

  // Progressive loader (newest-first)
  useEffect(() => {
    if (isPaused || currentLoading) return;
    const next = getNextMonthToLoad();
    if (!next) return;

    const timer = setTimeout(async () => {
      setCurrentLoading(next.month);
      try {
        const data = await queryClient.fetchQuery({
          queryKey: balanceSheetKeys.asOf(next.asOf),
          queryFn: () => fetchBalanceSheet(next.asOf),
          staleTime: 1000 * 60 * 5,
        });
        setLoaded((prev) => ({
          ...prev,
          [next.month]: {
            totalAssets: data.totalAssets,
            totalLiabilities: data.totalLiabilities,
            totalEquity: data.totalEquity,
            totalLiabilitiesAndEquity: data.totalLiabilitiesAndEquity,
            report: data,
          },
        }));
      } catch {
        setLoaded((prev) => ({
          ...prev,
          [next.month]: {
            totalAssets: 0,
            totalLiabilities: 0,
            totalEquity: 0,
            totalLiabilitiesAndEquity: 0,
            report: {
              companyName: '', reportName: '', reportDate: next.asOf, currency: 'CAD',
              assets: [], liabilitiesAndEquity: [],
              totalAssets: 0, totalLiabilities: 0, totalEquity: 0, totalLiabilitiesAndEquity: 0,
            },
          },
        }));
      } finally {
        setCurrentLoading(null);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isPaused, currentLoading, getNextMonthToLoad, queryClient]);

  const handleRefresh = useCallback(async () => {
    await Promise.allSettled(monthsList.map((m) => clearBalanceSheetCache(m.asOf)));
    queryClient.invalidateQueries({ queryKey: balanceSheetKeys.all });
    setLoaded({});
    setCurrentLoading(null);
    setIsPaused(false);
  }, [monthsList, queryClient]);

  const loadedCount = Object.keys(loaded).length;
  const totalCount = monthsList.length;
  const isAllLoaded = loadedCount === totalCount;

  // Step 6 — export CSV for comparative view
  function handleExportComparativeCSV() {
    const displayMonths = [...monthsList].reverse(); // newest first, matches table order
    const rows = buildComparativeRows(displayMonths, loaded);
    if (rows.length === 0) return;
    const columns: CSVColumn<Record<string, string | number>>[] = [
      { header: 'Line item', value: (r) => r['Line item'] },
      ...displayMonths.map((m) => ({
        header: m.label,
        value: (r: Record<string, string | number>) => r[m.label] ?? '',
      })),
      { header: 'Change', value: (r) => r['Change'] ?? '' },
    ];
    downloadCSV('comparative-balance-sheet.csv', toCSV(rows, columns));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">
          {isAllLoaded ? `${totalCount} periods` : `Loading ${loadedCount}/${totalCount}…`}
        </span>
        {!isAllLoaded && (
          <Button variant="outline" size="sm" onClick={() => setIsPaused((p) => !p)}>
            {isPaused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
        )}
        {/* Step 6 — Export CSV button */}
        <Button variant="outline" size="sm" onClick={handleExportComparativeCSV} disabled={loadedCount === 0}>
          <Download className="w-4 h-4 mr-1" />
          Export CSV
        </Button>
        <Select
          value={String(months)}
          onValueChange={(v) => {
            if (v === '') return;
            setMonths(parseInt(v, 10));
            setLoaded({});
            setCurrentLoading(null);
            setIsPaused(false);
          }}
        >
          <SelectTrigger className="h-8 w-[160px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem>
            <SelectItem value="24">Last 24 months</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" onClick={handleRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {!isAllLoaded && (
        <div className="w-full bg-muted rounded-full h-1.5">
          <div
            className="bg-primary h-1.5 rounded-full transition-all"
            style={{ width: `${(loadedCount / totalCount) * 100}%` }}
          />
        </div>
      )}

      {/* Step 4 — initial empty-state while no months are loaded */}
      {loadedCount === 0 ? (
        <div className="rounded-xl border bg-background p-12 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading periods…</span>
        </div>
      ) : (
        <ComparativeTable
          monthsList={monthsList}
          loaded={loaded}
          loadedCount={loadedCount}
          totalCount={totalCount}
          isAllLoaded={isAllLoaded}
          currentLoading={currentLoading}
          expanded={expanded}
          onExpandedChange={setExpanded}
        />
      )}
    </div>
  );
}

function ComparativeTable({
  monthsList,
  loaded,
  loadedCount,
  totalCount,
  isAllLoaded,
  currentLoading,
  expanded,
  onExpandedChange,
}: {
  monthsList: MonthEnd[];
  loaded: Record<string, MonthData>;
  loadedCount: number;
  totalCount: number;
  isAllLoaded: boolean;
  currentLoading: string | null;
  expanded: string[];
  onExpandedChange: (next: string[] | ((prev: string[]) => string[])) => void;
}) {
  // Newest → oldest, left to right
  const months = useMemo(() => [...monthsList].reverse(), [monthsList]);
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);

  function toggle(key: string) {
    onExpandedChange((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  const newestMonth = months[0]?.month;
  const oldestMonth = months[months.length - 1]?.month;
  const glStart = useMemo(() => glStartFor(months[months.length - 1]?.asOf ?? ''), [months]);
  const glEnd = months[0]?.asOf ?? '';

  // Section totals per month, straight from each report.
  function sectionTotal(section: SectionKey, m?: MonthData): number | null {
    if (!m) return null;
    if (section === 'assets') return m.totalAssets;
    if (section === 'liabilities') return m.totalLiabilities;
    return m.totalEquity;
  }

  // Aggregate leaf accounts for a section across all loaded months.
  function aggregate(section: SectionKey): AccountAgg[] {
    const map = new Map<string, AccountAgg>();
    months.forEach((m, idx) => {
      const data = loaded[m.month];
      if (!data) return;
      const rows = section === 'assets' ? data.report.assets : data.report.liabilitiesAndEquity;
      let inEquity = false;
      for (const r of rows) {
        if (r.type === 'subtotal' && /^total liabilities$/i.test(r.name)) {
          inEquity = true;
          continue;
        }
        if (r.type !== 'item') continue;
        const rowSection: SectionKey = section === 'assets' ? 'assets' : inEquity ? 'equity' : 'liabilities';
        if (rowSection !== section) continue;
        const key = r.accountId || `name:${r.name}`;
        let agg = map.get(key);
        if (!agg) {
          agg = { key, name: r.name, accountId: r.accountId, perMonth: {}, firstSeenIdx: idx };
          map.set(key, agg);
        }
        agg.perMonth[m.month] = (agg.perMonth[m.month] ?? 0) + r.value;
      }
    });
    // Sort by absolute latest balance desc, then first-seen
    return [...map.values()].sort((a, b) => {
      const av = Math.abs(a.perMonth[newestMonth] ?? 0);
      const bv = Math.abs(b.perMonth[newestMonth] ?? 0);
      return bv - av || a.firstSeenIdx - b.firstSeenIdx;
    });
  }

  const sections: { key: SectionKey; label: string; valueClass: string }[] = [
    { key: 'assets', label: 'Assets', valueClass: 'text-primary' },
    { key: 'liabilities', label: 'Liabilities', valueClass: 'text-warning' },
    {
      key: 'equity',
      label: 'Equity',
      // TODO GAP-FE-010: no purple semantic token — using text-secondary-foreground as nearest
      valueClass: 'text-secondary-foreground',
    },
  ];

  function changeOf(perMonth: Record<string, number>): number {
    return (perMonth[newestMonth] ?? 0) - (perMonth[oldestMonth] ?? 0);
  }

  function fmtCell(v: number | null): string {
    if (v === null) return '—';
    if (v === 0) return '';
    return formatCurrency(v);
  }

  // Balance check per month
  function balanceDelta(m?: MonthData): number | null {
    if (!m) return null;
    return m.totalAssets - m.totalLiabilitiesAndEquity;
  }

  return (
    <div className="bg-background rounded-xl shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        {/* Step 1 — min-w-full so table expands to natural width inside overflow-x-auto */}
        <table className="min-w-full text-sm border-separate border-spacing-0">
          {/* Step 8 — accessible caption */}
          <caption className="sr-only">Comparative Balance Sheet by Month</caption>
          <thead className="bg-muted/50">
            <tr className="text-xs font-semibold text-muted-foreground">
              <th className="sticky left-0 z-10 bg-muted px-3 py-3 text-left border-b min-w-[200px]">
                Line item
              </th>
              {months.map((m) => (
                <th key={m.month} className="px-3 py-3 text-right border-b min-w-[110px]">
                  <div className="inline-flex items-center gap-1 justify-end">
                    {m.label}
                    {currentLoading === m.month && <RefreshCw className="w-3 h-3 animate-spin text-primary" />}
                  </div>
                </th>
              ))}
              <th className="sticky right-0 z-10 bg-muted px-3 py-3 text-right border-b border-l min-w-[120px]">
                Change{!isAllLoaded && ` (${loadedCount}/${totalCount})`}
              </th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => {
              const isOpen = expandedSet.has(section.key);
              const detail = isOpen ? aggregate(section.key) : [];
              return (
                <Fragment key={section.key}>
                  {/* Step 8 — toggle moved into a button inside the first cell */}
                  <tr className={cn('hover:bg-muted/40 font-medium', isOpen && 'bg-accent')}>
                    <td className={cn('sticky left-0 z-10 px-3 py-2 border-b', isOpen ? 'bg-accent' : 'bg-background')}>
                      {/* Step 8 — real <button> with aria-expanded + focus-visible ring */}
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-label={`Toggle ${section.label} details`}
                        onClick={() => toggle(section.key)}
                        className="flex items-center gap-1 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded"
                      >
                        {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                        Total {section.label}
                      </button>
                    </td>
                    {months.map((m) => {
                      const v = sectionTotal(section.key, loaded[m.month]);
                      return (
                        <td key={m.month} className={cn('px-3 py-2 text-right tabular-nums border-b', section.valueClass)}>
                          {v === null ? '—' : formatCurrency(v)}
                        </td>
                      );
                    })}
                    <td className={cn('sticky right-0 z-10 px-3 py-2 text-right tabular-nums font-semibold border-b border-l', isOpen ? 'bg-accent' : 'bg-background', changeTone(sectionChange(section.key, loaded, newestMonth, oldestMonth)))}>
                      {fmtChange(sectionChange(section.key, loaded, newestMonth, oldestMonth))}
                    </td>
                  </tr>
                  {isOpen && detail.map((agg) => (
                    <tr key={`${section.key}-${agg.key}`} className="bg-muted/5 text-xs text-muted-foreground">
                      <td className="sticky left-0 z-10 bg-background px-3 py-1.5 border-b pl-8">
                        {agg.accountId ? (
                          <Link
                            to={`/acc/qbo-pnl/gl/${agg.accountId}?start=${glStart}&end=${glEnd}&name=${encodeURIComponent(agg.name)}`}
                            className="hover:underline"
                            /* TODO GAP-FE-010: hover:bg-blue-100/hover:text-blue-700 has no semantic token */
                            onClick={(e) => e.stopPropagation()}
                          >
                            {agg.name}
                          </Link>
                        ) : (
                          agg.name
                        )}
                      </td>
                      {months.map((m) => {
                        const v = loaded[m.month] ? agg.perMonth[m.month] ?? 0 : null;
                        return (
                          <td key={m.month} className="px-3 py-1.5 text-right tabular-nums border-b">
                            {fmtCell(v)}
                          </td>
                        );
                      })}
                      <td className={cn('sticky right-0 z-10 bg-background px-3 py-1.5 text-right tabular-nums border-b border-l', changeTone(changeOf(agg.perMonth)))}>
                        {fmtChange(changeOf(agg.perMonth))}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}

            {/* Total Liabilities & Equity — sticky cells use fully-opaque bg-muted to prevent column bleed-through */}
            <tr className="font-semibold bg-muted/30">
              <td className="sticky left-0 z-10 bg-muted px-3 py-2 border-b">
                <div className="flex items-center gap-1 pl-[18px]">Total Liabilities &amp; Equity</div>
              </td>
              {months.map((m) => {
                const d = loaded[m.month];
                return (
                  <td key={m.month} className="px-3 py-2 text-right tabular-nums border-b">
                    {d ? formatCurrency(d.totalLiabilitiesAndEquity) : '—'}
                  </td>
                );
              })}
              <td className="sticky right-0 z-10 bg-muted px-3 py-2 text-right tabular-nums border-b border-l">
                {fmtChange(sectionChangeRaw(loaded, newestMonth, oldestMonth, (d) => d.totalLiabilitiesAndEquity))}
              </td>
            </tr>

            {/* Balance check */}
            <tr className="text-xs">
              <td className="sticky left-0 z-10 bg-background px-3 py-2 border-b text-muted-foreground">
                <div className="flex items-center gap-1 pl-[18px]">Balance check (A − L&amp;E)</div>
              </td>
              {months.map((m) => {
                const delta = balanceDelta(loaded[m.month]);
                const ok = delta !== null && Math.abs(delta) < 0.01;
                return (
                  <td key={m.month} className="px-3 py-2 text-right tabular-nums border-b">
                    {delta === null ? (
                      '—'
                    ) : ok ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-success-dark inline" />
                    ) : (
                      <span className="text-destructive inline-flex items-center gap-1 justify-end">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {formatCurrency(delta)}
                      </span>
                    )}
                  </td>
                );
              })}
              <td className="sticky right-0 z-10 bg-background px-3 py-2 border-b border-l" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function sectionChange(section: SectionKey, loaded: Record<string, MonthData>, newest: string, oldest: string): number {
  const pick = (d?: MonthData) =>
    !d ? 0 : section === 'assets' ? d.totalAssets : section === 'liabilities' ? d.totalLiabilities : d.totalEquity;
  return pick(loaded[newest]) - pick(loaded[oldest]);
}

function sectionChangeRaw(
  loaded: Record<string, MonthData>,
  newest: string,
  oldest: string,
  pick: (d: MonthData) => number,
): number {
  const n = loaded[newest] ? pick(loaded[newest]) : 0;
  const o = loaded[oldest] ? pick(loaded[oldest]) : 0;
  return n - o;
}

function fmtChange(v: number): string {
  if (Math.abs(v) < 0.01) return '—';
  return `${v > 0 ? '+' : ''}${formatCurrency(v)}`;
}

function changeTone(v: number): string {
  if (Math.abs(v) < 0.01) return 'text-muted-foreground/60';
  return v > 0 ? 'text-success-dark' : 'text-destructive';
}

// ============================================================================
// Shared presentational pieces
// ============================================================================

function SummaryCard({
  title,
  value,
  icon,
  loading,
}: {
  title: string;
  value: number;
  icon?: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <div className="bg-background rounded-xl shadow-sm border p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</span>
        {icon}
      </div>
      <div className="text-2xl font-bold mt-2 tabular-nums">
        {loading ? <span className="text-muted-foreground/40">—</span> : formatCurrency(value)}
      </div>
    </div>
  );
}

function BalanceCheckCard({ delta, balanced, loading }: { delta: number; balanced: boolean; loading?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-xl shadow-sm border p-5',
        loading
          ? 'bg-background'
          : balanced
            ? 'bg-success-light border-success'
            : 'bg-danger-light border-danger',
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Balance Check</span>
        {!loading && (balanced ? <CheckCircle2 className="w-4 h-4 text-success-dark" /> : <AlertTriangle className="w-4 h-4 text-destructive" />)}
      </div>
      <div className={cn('text-2xl font-bold mt-2 tabular-nums', !loading && (balanced ? 'text-success-dark' : 'text-destructive'))}>
        {loading ? <span className="text-muted-foreground/40">—</span> : balanced ? 'Balanced' : formatCurrency(delta)}
      </div>
    </div>
  );
}

function SectionCard({
  title,
  total,
  totalLabel,
  totalClass,
  children,
}: {
  title: string;
  total: number;
  totalLabel: string;
  totalClass?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-background rounded-xl shadow-sm border overflow-hidden">
      <div className="bg-muted/50 border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div className="p-4 text-sm space-y-1">
        {children}
        <div className={cn('flex justify-between py-2 font-bold border-t-2 mt-3', totalClass)}>
          <span>{totalLabel}</span>
          <span className="tabular-nums">{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
}

function AccountRow({ row, start, end }: { row: BalanceSheetRow; start: string; end: string }) {
  const base = cn(
    'flex justify-between py-1',
    row.type === 'header' && 'font-medium text-foreground mt-2',
    row.type === 'subtotal' && 'font-medium text-muted-foreground border-t pt-2 mt-1',
    row.type === 'item' && 'text-muted-foreground',
  );
  const content = (
    <>
      <span className="truncate mr-2">{row.name}</span>
      {row.value !== 0 && <span className="tabular-nums shrink-0">{formatCurrency(row.value)}</span>}
    </>
  );
  if (row.accountId && row.type === 'item') {
    return (
      <Link
        to={`/acc/qbo-pnl/gl/${row.accountId}?start=${start}&end=${end}&name=${encodeURIComponent(row.name)}`}
        className={cn(
          base,
          // TODO GAP-FE-010: hover:bg-blue-100/hover:text-blue-700 has no semantic token
          'hover:bg-blue-100 dark:hover:bg-blue-950/40 hover:text-blue-700 rounded px-1 -mx-1',
        )}
        style={{ paddingLeft: `${row.depth * 12}px` }}
      >
        {content}
      </Link>
    );
  }
  return (
    <div className={base} style={{ paddingLeft: `${row.depth * 12}px` }}>
      {content}
    </div>
  );
}
